/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import { DateOfBirthInput } from 'src/components/ts-components/DateOfBirthInput'
import { TextInput } from 'src/components/ts-components/TextInput'

type SelfRegisterStepProps = {
  /** Invitee's first name, prefilled from the invite. */
  nameFirst: string
  setNameFirst: (value: string) => void
  /** Invitee's last name, prefilled from the invite. */
  nameLast: string
  setNameLast: (value: string) => void
  /** YYYY-MM-DD; empty until the user completes all three dropdowns. */
  birthDate: string
  setBirthDate: (value: string) => void
  password: string
  setPassword: (value: string) => void
  confirmPassword: string
  setConfirmPassword: (value: string) => void
  /** Submits: validate → signup → login. */
  onNext: () => void
  /** Return to the Welcome step. */
  onBack: () => void
  /** Field names currently in error, consumed by the shared inputs. */
  fieldErrors?: Record<string, string>
  /** Per-field error messages keyed by field name. */
  customError?: Record<string, string>
  /** Disables the form while the signup round trip is in flight. */
  loading?: boolean
}

/**
 * Self-registration step for an invitee who answered "yes, it's me" on an
 * invite that was flagged underage only because it carried no birth_date.
 *
 * Rather than dead-ending them on UnderAgeError, we collect the same fields
 * the regular invite register form does — name, password, DOB — and run the
 * identical validate → signup → login sequence. If the DOB they enter turns
 * out to be under 13, the backend rejects it through `invalid_fields` and the
 * error surfaces on the DOB input.
 */
const SelfRegisterStep = ({
  nameFirst,
  setNameFirst,
  nameLast,
  setNameLast,
  birthDate,
  setBirthDate,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  onNext,
  onBack,
  fieldErrors = {},
  customError = {},
  loading = false,
}: SelfRegisterStepProps) => {
  return (
    <div css={containerStyle}>
      <Text
        variant='heading3'
        weight='semiBold'
        color={colors.NEUTRAL_0}
        style={'text-align: center;'}
      >
        {capitalizeWords('Create Your Account To Accept Invite')}
      </Text>

      <div css={rowStyle}>
        <div css={halfColumnStyle}>
          <TextInput
            name='name_first'
            customError={customError}
            fieldErrors={fieldErrors}
            label={capitalizeWords('First Name')}
            setValue={setNameFirst}
            value={nameFirst}
            placeholder={capitalizeWords('First Name')}
            autoComplete='given-name'
            disabled={loading}
            required
          />
        </div>
        <div css={halfColumnStyle}>
          <TextInput
            name='name_last'
            customError={customError}
            fieldErrors={fieldErrors}
            label={capitalizeWords('Last Name')}
            setValue={setNameLast}
            value={nameLast}
            placeholder={capitalizeWords('Last Name')}
            autoComplete='family-name'
            disabled={loading}
            required
          />
        </div>
      </div>

      {/* DOB sits above the passwords, matching the invitation signup form. */}
      <div css={rowStyle}>
        <div css={fullColumnStyle}>
          <DateOfBirthInput
            name='birth_date'
            value={birthDate}
            setValue={setBirthDate}
            fieldErrors={fieldErrors}
            customError={customError}
            label={capitalizeWords('Date Of Birth')}
            disabled={loading}
            required
          />
        </div>
      </div>

      <div css={rowStyle}>
        <div css={halfColumnStyle}>
          <TextInput
            name='password'
            type='password'
            customError={customError}
            fieldErrors={fieldErrors}
            label={capitalizeWords('Password')}
            setValue={setPassword}
            value={password}
            placeholder={capitalizeWords('Password')}
            autoComplete='new-password'
            disabled={loading}
            required
          />
        </div>
        <div css={halfColumnStyle}>
          <TextInput
            name='confirm_password'
            type='password'
            customError={customError}
            fieldErrors={fieldErrors}
            label={capitalizeWords('Confirm Password')}
            setValue={setConfirmPassword}
            value={confirmPassword}
            placeholder={capitalizeWords('Confirm Password')}
            autoComplete='new-password'
            disabled={loading}
            required
          />
        </div>
      </div>

      <div css={footerStyle}>
        <div css={backColumnStyle}>
          <Button
            text='Back'
            variant='secondary'
            onClick={onBack}
            disabled={loading}
          />
        </div>
        <div css={nextColumnStyle}>
          <Button
            text='Create Account'
            variant='primary'
            onClick={onNext}
            disabled={loading}
            isLoading={loading}
          />
        </div>
      </div>

      <div css={termsStyle}>
        <Text
          variant='paragraphXSmall'
          weight='regular'
          color={colors.NEUTRAL_50}
          style={'text-align: center;'}
        >
          By creating an account you agree to our{' '}
          <a
            href='/docs/eula.pdf'
            target='_blank'
            rel='noreferrer noopener'
            css={termsLinkStyle}
          >
            Terms of Use
          </a>
        </Text>
      </div>
    </div>
  )
}

export default SelfRegisterStep

// ---- styled ----
/*
 * The flow's shell owns the page padding, centering and width cap for this
 * step, so the container only stacks its own children. Rows are spaced with a
 * gap rather than per-row margins so a conditionally hidden row closes its own
 * space.
 */
const containerStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`

const rowStyle = css`
  display: flex;
  gap: 16px;

  /* Stack the paired fields once the column gets too narrow to hold two
     side by side comfortably. */
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

// min-width: 0 lets the flex children actually shrink — without it their
// intrinsic input width keeps the row from narrowing and forces overflow.
const halfColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 50%;
  min-width: 0;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const fullColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
`

const footerStyle = css`
  display: flex;
  gap: 8px;
  width: 100%;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

// Even split — the two actions carry equal weight on a form this narrow.
const backColumnStyle = css`
  display: flex;
  flex: 1;
  min-width: 0;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const nextColumnStyle = css`
  display: flex;
  flex: 1;
`

const termsStyle = css`
  display: flex;
  justify-content: center;
`

const termsLinkStyle = css`
  color: ${colors.NEUTRAL_0};
  text-decoration: underline;
  text-underline-offset: 2px;
`
